export type TimeStamps = {
  createdTime?: string
  updatedTime?: string
}

export type Assignment = {
  id: string
  name: string
  dueDate: string
  linkedServiceID: string
  users: string[]
  courses: string[]
} & TimeStamps

export type RecurrenceType = 'Monthly' | 'Yearly' | 'NoRecurrence'

export type Recurrence = {
  recurrenceType: RecurrenceType
  recurrenceInterval: number
  recurrenceEndDate?: string
  dayOfMonth: number
}

export type ScheduledAssignmentStatus =
  'Pending' | 'Activated' | 'Cancelled' | 'Complete'

export type ScheduledAssignment = {
  id: string
  name: string
  dueDaysAfterGoLive: number
  goLiveDate: string
  calculatedDueDate: string
  courseIDs: string[]
  userIDs: string[]
  linkedServiceID: string
  recurrence?: Recurrence
  status: ScheduledAssignmentStatus
  createdAssignments: string[]
} & TimeStamps

export type TrainingSeries = {
  id: string
  name: string
  description?: string
  linkedServiceID: string
  courseIDs: string[]
} & TimeStamps

export type Class = {
  id: string
  name: string
  description: string
  courseID: string
  sectionID?: string
  imageName: string
  sequence: number
  published: boolean
} & TimeStamps

export type ClassUpdated = {
  id: string
  name: string
  description: string
  imageName: string
  sequence: number
  courseID: string
} & TimeStamps

export type ClassStreamEvent = {
  id: string
  status: string
  userID: string
  classID: string
} & TimeStamps

export type CourseStreamEvent = {
  id: string
  status: string
  userID: string
  courseID: string
} & TimeStamps

export type Course = {
  id: string
  sectionID: string
  section?: string
  linkedServiceID?: string
  length: number
  name: string
  displayName: string
  banner?: string
  imageName: string
  templateID: string
  description?: string
  objectives?: string[]
  references?: string[]
  published: 'published' | 'unpublished'
  version?: number
  publishedVersion?: number
} & TimeStamps

export type Credential = {
  id: string
  userID: string
  type: string | { other: string }
  expiration: string
  credentialIDNumber: string
  imageName: string
} & TimeStamps

export type FlatCredential = Credential & {
  credentialType: string
}

export const CREDENTIAL_TYPES = [
  'BLS',
  'ACLS',
  'PALS',
  'NRP',
  'PHTLS',
  'CCP-C',
  'FP-C',
  'CFRN',
  'Other',
] as const

export type CredentialType =
  | { kind: 'BLS' }
  | { kind: 'ACLS' }
  | { kind: 'PALS' }
  | { kind: 'NRP' }
  | { kind: 'PHTLS' }
  | { kind: 'CCP-C' }
  | { kind: 'FP-C' }
  | { kind: 'CFRN' }
  | { kind: 'Other'; value: string }

export type CredentialV2 = {
  id: string
  userID: string
  type: CredentialType
  issueDate?: string
  expiration: string
  credentialIDNumber: string
  imageName: string
} & TimeStamps

export type DisplayRank = 'Primary' | 'Secondary' | 'Additional'

export type License = {
  id: string
  userID: string
  imageName?: string
  displayRank: DisplayRank
  type: string
  state: string
  issueDate?: string
  expiration: string
  number: string
} & TimeStamps

export type Certificate = {
  id: string
  userID: string
  courseID: string
  surveyID: string
  filePath: string
  courseData?: Course
} & TimeStamps

export type RecordedCertificate = {
  id: string
  name: string
  userID?: string
  hours: number
  section: string
  fileName: string
  dateCompleted: string
} & TimeStamps

export type LiveCertificate = {
  id: string
  name: string
  userID?: string
  hours: number
  fileName: string
  dateCompleted: string
} & TimeStamps

export type ExternalCertificate = {
  id: string
  userID: string
  name: string
  length: number
  hourType:
    | 'AirwayRespiratoryVentilation'
    | 'Medical'
    | 'Trauma'
    | 'Operations'
    | 'CriticalCare'
    | 'OBGYN'
    | 'PediatricsNeonatal'
    | 'Other'
  courseCode: string
  courseType: 'Live' | 'Recorded'
  fileName: string
  dateCompleted: string
} & TimeStamps

export type LearningPath = {
  id: string
  name: string
  category: string
  imageName: string
  courseIDs: string[]
  linkedServiceID?: string
} & TimeStamps

export type SpecialCertification = {
  id: string
  name: string
  courseIDs: string[]
} & TimeStamps

export type IBSCCertification = {
  certificates: ({
    id: string
    specialCertificationID: string
    userID: string
    filePath: string
  } & TimeStamps)[]
  status: 'Completed' | 'In-Progress'
}

export type UserSubscription = {
  id: string
  userID: string
  subscriptionID: string
  subscriptionStatus: 'active' | 'pending' | 'trial' | 'cancelled'
  subscriptionEndDate: string
  customerID: string
} & TimeStamps

export type UserAccessLevel = {
  id: string
  userID: string
  accessLevel: 'full' | 'limited'
} & TimeStamps

export type LinkedService = {
  id: string
  name: string
  address?: string
  city?: string
  state?: string
  zip?: string
  imageName: string
  status: 'active' | 'inactive'
  credentialVerification: 'disabled' | 'full'
  licenseVerification: 'disabled' | 'full'
  nremtVerification: 'disabled' | 'full'
  userCap?: number
  adminUserID: string
  organizationID: string
  completionThreshold: number
  applyThresholdToScheduledAssignments: boolean
  certificateImageName?: string
} & TimeStamps

export type LinkedServiceUser = {
  id: string
  userID: string
  linkedServiceID: string
} & TimeStamps

export type ConnectClient = {
  id: string
  clientName: string
  clientID: string
} & TimeStamps

export type ConnectClientLinkedService = {
  id: string
  connectClientID: string
  linkedServiceID: string
  linkedServiceName: string
} & TimeStamps

export type ProfileImage = {
  id: string
  imageName: string
  userID: string
} & TimeStamps

export type Section = {
  id: string
  name: string
  description: string
  libraryId: string
  imageName: string
  linkedServiceID?: string
  pageRank: number
} & TimeStamps

export type User = {
  id: string
  email: string
  primaryEmail: string
  firstName: string
  lastName: string
  address?: string
  city?: string
  state?: string
  zip?: string
  phone?: string
  nremtCertificationNumber?: string
  nremtCertificationDate?: string
  nremtIssueDate?: string
  licenseExpiration?: string
  licenseNumber?: string
  licenseState?: string
  licenseType?: string
} & TimeStamps

export type UserV2 = {
  id: string
  email: string
  primaryEmail: string
  firstName: string
  lastName: string
  address?: string
  city?: string
  state?: string
  zip?: string
  phone?: string
} & TimeStamps

export type LiveClass = {
  id: string
  meetingUrl: string
  imageName: string
  name: string
  section: string
  quizPasscode: string
  quizID: string
  titleScreenID: string
} & TimeStamps

export type LiveClassSession = {
  id: string
  classID: string
  time: string
} & TimeStamps

export type AccreditationType =
  | 'capce'
  | 'newJersey'
  | 'newYork'
  | 'virginia'
  | 'ceBroker'
  | 'accredited'
  | 'notAccredited'

export type CAPCEAccreditation = {
  id: string
  courseID: string
  courseCode: string
  accreditationType: 'capce'
} & TimeStamps

export type NewJerseyAccreditation = {
  id: string
  courseID: string
  courseCode: string
  accreditationType: 'newJersey'
} & TimeStamps

type TopicArea =
  | 'Preparatory'
  | 'Airway'
  | 'Pharmacology | Med Admin | Emergency Meds'
  | 'Immunology'
  | 'Toxicology'
  | 'Endocrine'
  | 'Neurology'
  | 'Abdominal | Geni-Renal | GI | Hematology'
  | 'Respiratory'
  | 'Psychiatric'
  | 'Cardiology'
  | 'Shock and Resuscitation'
  | 'Trauma'
  | 'Geriatrics'
  | 'OB | Neonate | Pediatrics'
  | 'Special Needs Pt'
  | 'EMS Operations'
  | 'Mental Health of EMT'
  | 'Patient Lifting and Moving'
  | 'Pediatric Safe Transport'
  | 'Emergency Vehicle Driver Training'

export type NewYorkAccreditation = {
  id: string
  courseID: string
  topicArea: TopicArea
  accreditationType: 'newYork'
} & TimeStamps

export type VirginiaAccreditation = {
  id: string
  courseID: string
  topic: string
  subTopic: string
  areaNumberALS: string
  areaNumberBLS: string
  accreditationType: 'virginia'
} & TimeStamps

export type CEBrokerAccreditation = {
  id: string
  courseID: string
  state: 'Florida' | 'Kentucky' | 'Tennessee'
  courseCode: string
  professionCode: string
  subjectArea: string
  accreditationType: 'ceBroker'
} & TimeStamps

export type AccreditedAccreditation = {
  id: string
  courseID: string
  courseCode: string
  accreditationType: 'accredited'
} & TimeStamps

export type NotAccredited = {
  id: string
  courseID: string
  courseCode: string
  accreditationType: 'notAccredited'
} & TimeStamps

export type Accreditation = {
  capce?: CAPCEAccreditation
  newJersey?: NewJerseyAccreditation
  newYork?: NewYorkAccreditation
  virginia?: VirginiaAccreditation
  ceBroker?: CEBrokerAccreditation
  accredited?: AccreditedAccreditation
  notAccredited?: NotAccredited
}

export type FlatAccreditation =
  NotAccredited | CAPCEAccreditation | VirginiaAccreditation

export type Template = {
  id: string
  name: string
  statement1: string
  statement2: string
  linkedServiceID?: string
} & TimeStamps

export type TitleScreen = {
  id?: string
  name: string
  description: string
  content: string
  classID: string
  imageName?: string
  imageUrl?: string
} & TimeStamps

export type Video = {
  id: string
  name: string
  description: string
  classID: string
  fileName: string
  fileType: string
} & TimeStamps

export type TextContent = {
  id: string
  classID: string
  name: string
  description: string
  content: string
} & TimeStamps

export type ShowNotes = {
  id: string
  classID: string
  name: string
  description: string
} & TimeStamps

export type QuizQuestionAnswer = {
  id: string
  text: string
  sequence: number
  isAnswer: boolean
} & TimeStamps

export type QuizQuestion = {
  id: string
  text: string
  sequence: number
  choices: QuizQuestionAnswer[]
  explanationText: string
  answerChoiceID: string
  figureImageName?: string
} & TimeStamps

export type Quiz = {
  id: string
  classID: string
  name: string
  description: string
  passingScore: number
  questions: QuizQuestion[]
  courseCode?: string // deprecated; not in use in V3
} & TimeStamps

export type StreamStep = {
  id: string
  type:
    | 'Video'
    | 'TextContent'
    | 'TitleScreen'
    | 'Quiz'
    | 'ShowNotes'
    | 'Unassigned'
  stepIndex: number
  classID?: string
  videoID?: string
  textContentID?: string
  titleScreenID?: string
  quizID?: string
  showNotesID?: string
} & TimeStamps

export type Stream = {
  id: string
  classID: string
  steps: StreamStep[]
} & TimeStamps

export type SeriesCourse = {
  id: string
  seriesID: string
  courseID: string
  season: number
  episode: number
} & TimeStamps

export type Series = {
  id: string
  name: string
  banner: string
  thumbnail: string
  mobileBanner: string
  description: string
  published: 'published' | 'unpublished'
  courses: SeriesCourse[]
  sequence: number
} & TimeStamps

export type LinkedServiceSeries = Series & {
  linkedServiceID: string
}

export type AssignmentDueNotification = {
  kind: 'assignmentDue'
  id: string
  userID: string
  status: 'unread' | 'read'
  assignmentID: string
  dueDate: string
  name: string
}

export type AssignmentAssignedNotification = {
  kind: 'assignmentAssigned'
  id: string
  userID: string
  status: 'unread' | 'read'
  assignmentID: string
  dueDate: string
  name: string
}

export type CredentialExpiringNotification = {
  kind: 'credentialExpiring'
  id: string
  userID: string
  status: 'unread' | 'read'
  credentialID: string
  credentialNumber: string
  expirationDate: string
  type: string
}

export type ComplianceAssignedNotification = {
  kind: 'compliance'
  id: string
  userID: string
  status: 'unread' | 'read'
  complianceID: string
  name: string
}

export type Notification =
  | AssignmentDueNotification
  | AssignmentAssignedNotification
  | CredentialExpiringNotification
  | ComplianceAssignedNotification

export type InPersonEventLocationData = {
  name: string
  address: string
  city: string
  state: string
  zip: string
  latitude?: number
  longitude?: number
}

export type InPersonEventLocation = {
  kind: 'inPerson'
} & InPersonEventLocationData

export type VirtualEventLocationData = {
  name: string
  url?: string
}

export type VirtualEventLocation = {
  kind: 'virtual'
} & VirtualEventLocationData

export type HybridEventLocation = {
  kind: 'hybrid'
} & VirtualEventLocationData &
  InPersonEventLocationData

export type EventType =
  VirtualEventLocation | InPersonEventLocation | HybridEventLocation

export type AttendanceTrackingMechanism =
  { kind: 'quizPasscode'; quizPasscode: string } | { kind: 'managerTracked' }

export type LifecycleState = 'active' | 'preArchive' | 'archive'

export type Event = {
  id: string
  linkedServiceID?: string
  banner?: string
  thumbnail?: string
  name: string
  description: string
  hours: number
  category:
    | { kind: 'airway|Respiratory|Ventilation' }
    | { kind: 'medical' }
    | { kind: 'trauma' }
    | { kind: 'operations' }
    | { kind: 'criticalCare' }
    | { kind: 'cardiovascular' }
    | { kind: 'pediatrics' }
    | { kind: 'other'; other: string }
  categoryName: string
  attendanceTrackingMechanism: AttendanceTrackingMechanism
  location: EventType
  eventType: string
  featured: boolean
  instructorIDs: string[]
  lifecycleState?: LifecycleState
} & TimeStamps

export type EventTypeName = 'virtual' | 'inPerson' | 'hybrid'

export type EventSession = {
  id: string
  name?: string
  eventID: string
  sessionTime: string
  endTime?: string
  registrationDeadline?: string
  timeZone: string
  instructorIDs: string[]
} & TimeStamps

export type EventInviteStatus =
  'invited' | 'accepted' | 'declined' | 'waitlisted' | 'attended'

export type EventInvite = {
  id: string
  userID: string
  sessionID: string
  status?: EventInviteStatus
} & TimeStamps

export type EventCompletionEvent = {
  id: string
  sessionID: string
  userID: string
  eventID: string
  status: string
} & TimeStamps

export type EventCERecord = {
  id: string
  eventID: string
  ceRecordID: string
} & TimeStamps

export type ANCCAccreditation = {
  kind: 'ancc'
  id: string
  courseCode: string
  expirationDate: string
  targetAudience: string[]
  educationalNeeds: string[]
  competency: string
  professionalSource: string
} & TimeStamps

export type CAPCEAccreditationV2 = {
  kind: 'capce'
  id: string
  courseCode: string
  expirationDate?: string
  applicationDate?: string
  submissionDate?: string
  topicArea: string
  classType: string
  allClearDate?: string
  goLiveDate?: string
} & TimeStamps

export type VirginiaAccreditationV2 = {
  kind: 'virginia'
  id: string
  topic: string
  subTopic: string
  areaNumberALS?: string
  areaNumberBLS?: string
} & TimeStamps

export type CEBrokerAccreditationV2 = {
  kind: 'ceBroker'
  id: string
  courseCode: string
  professionCode: string
  subjectArea: string
  state: string
} & TimeStamps

export type NewJerseyAccreditationV2 = {
  kind: 'newJersey'
  id: string
  courseCode: string
} & TimeStamps

export type NewYorkAccreditationV2 = {
  kind: 'newYork'
  id: string
  topicArea: string
} & TimeStamps

export type NotAccreditedV2 = {
  kind: 'notAccredited'
  id: string
  courseCode: string
} & TimeStamps

export type AccreditedAccreditationV2 = {
  kind: 'accredited'
  id: string
  courseCode: string
} & TimeStamps

export type AccreditationV2 =
  | ANCCAccreditation
  | CAPCEAccreditationV2
  | VirginiaAccreditationV2
  | CEBrokerAccreditationV2
  | NewJerseyAccreditationV2
  | NewYorkAccreditationV2
  | AccreditedAccreditationV2
  | NotAccreditedV2

export type EventAccreditation = {
  eventID: string
  accreditationID: string
}

export type CourseAccreditation = {
  courseID: string
  accreditationID: string
}

export type QuizV2 = {
  id: string
  name?: string
  passingScore: number
  displayedQuestions: number
  randomizeQuestions: boolean
} & TimeStamps

export type QuizQuestionV2 = {
  id: string
  quizID: string
  text: string
  sequence: number
  explanation: string
  imageName?: string
  videoName?: string
} & TimeStamps

export type QuestionAnswer = {
  id: string
  questionID: string
  text: string
  sequence: number
} & TimeStamps

export type CorrectAnswer = {
  id: string
  questionID: string
  answerID: string
} & TimeStamps

export type CategoryResponse = {
  standard: string[]
  custom: string[]
}

export type QuestionData = {
  quizQuestion: QuizQuestionV2
  questionAnswers: QuestionAnswer[]
  correctAnswer: CorrectAnswer
}

export type QuestionSessionData = {
  quizQuestion: QuizQuestionV2
  questionAnswers: QuestionAnswer[]
  isCurrent: boolean
}

export type QuizSessionAnswer = {
  id: string
  sessionID: string
  answerID: string
  questionID: string
  isCorrect: boolean
} & TimeStamps

export type QuizSessionResult = {
  id: string
  sessionID: string
  score: number
  passingScore: number
  passed: boolean
} & TimeStamps

export type CEType = 'Event' | 'Course'

export type SurveyV2 = {
  id: string
  ceType: CEType
  userID: string
  topicsThoroughAndInteresting?: number
  presenterGoodUnderstanding?: number
  videoQualityProfessional?: number
  benefitCareerGoals?: number
  knowledgePositiveImpactPractice?: number
  activityPositiveImpactPractice?: number
  confusingTopics?: string
  thingsToBeContinued?: string
  thingsToBeImproved?: string
  explainImpactPractice?: string
} & TimeStamps

export type HeroSlide = {
  id: string
  title: string
  description: string
  imageName: string
  videoSrc: string
  startTime: number
  endTime: number
  seriesID: string
  buttonText: string
  sequence: number
  published: boolean
} & TimeStamps

export type Instructor = {
  id: string
  name: string
  linkedServiceID?: string
  credentials?: string
} & TimeStamps

export type Objective = {
  id: string
  syllabusID: string
  text: string
  sequence: number
} & TimeStamps

export type Reference = {
  id: string
  text: string
  linkedServiceID?: string
} & TimeStamps

export type Syllabus = {
  id: string
  text?: string
  practiceGap?: string
  practiceGapEvidence?: string
  learnerEngagement?: string
  learningOutcomes?: string
  objectives?: Objective[]
  references?: Reference[]
} & TimeStamps

export type SyllabusReference = {
  id: string
  referenceID: string
  syllabusID: string
  sequence: number
} & TimeStamps

export type ContentComplianceStatus =
  'new' | 'approved' | 'rejected' | 'updated' | 'unapproved'

export type EventCompliance = {
  eventID: string
  name: ContentComplianceStatus
  description: ContentComplianceStatus
  hours: ContentComplianceStatus
  category: ContentComplianceStatus
  instructors: ContentComplianceStatus
  thumbnail: ContentComplianceStatus
  banner: ContentComplianceStatus
  template: ContentComplianceStatus
  quiz: ContentComplianceStatus
  ancc: ContentComplianceStatus
  capce: ContentComplianceStatus
  practiceGap: ContentComplianceStatus
  practiceGapEvidence: ContentComplianceStatus
  learnerEngagement: ContentComplianceStatus
  learningOutcomes: ContentComplianceStatus
  objectives: ContentComplianceStatus
  references: ContentComplianceStatus
}

export type EventComplianceEvent = {
  id: string
  eventID: string
  actorID: string
  status: ContentComplianceStatus
  complianceFeedback?: string
} & TimeStamps

export type CourseV2Compliance = {
  courseID: string
  name: ContentComplianceStatus
  nameFeedback?: string
  description: ContentComplianceStatus
  descriptionFeedback?: string
  hours: ContentComplianceStatus
  hoursFeedback?: string
  thumbnail: ContentComplianceStatus
  thumbnailFeedback?: string
  banner: ContentComplianceStatus
  bannerFeedback?: string
  template: ContentComplianceStatus
  templateFeedback?: string
  section: ContentComplianceStatus
  sectionFeedback?: string
  practiceGap: ContentComplianceStatus
  practiceGapFeedback?: string
  practiceGapEvidence: ContentComplianceStatus
  practiceGapEvidenceFeedback?: string
  learnerEngagement: ContentComplianceStatus
  learnerEngagementFeedback?: string
  learningOutcomes: ContentComplianceStatus
  learningOutcomesFeedback?: string
  objectives: ContentComplianceStatus
  objectivesFeedback?: string
  references: ContentComplianceStatus
  referencesFeedback?: string
  instructors: ContentComplianceStatus
  instructorsFeedback?: string
  syllabusDescription: ContentComplianceStatus
  syllabusDescriptionFeedback?: string
  // Set by the backend after any content write (videos, show notes)
  content?: ContentComplianceStatus
  contentFeedback?: string
  // Set by the backend after quiz / accreditation migration
  quiz?: ContentComplianceStatus
  quizFeedback?: string
  capce?: ContentComplianceStatus
  capceFeedback?: string
  ancc?: ContentComplianceStatus
  anccFeedback?: string
}

export type PublishReadinessItem = {
  field: string
  status: 'New' | 'Updated' | 'Approved' | 'Rejected' | 'Unapproved'
  approved: boolean
}

export type CourseV2PublishReadiness = {
  courseID: string
  readyToPublish: boolean
  items: PublishReadinessItem[]
}

export type PlanningCourseV2 = {
  id: string
  sectionID: string
  linkedServiceID: string | null
  length: number
  name: string
  banner: string | null
  imageName: string
  templateID: string
  version: number
  publishedVersion: number
  published: 'published' | 'unpublished'
  createdTime: string | null
  updatedTime: string | null
  lifecycleState?: LifecycleState
}

export type LegacyTitleScreen = {
  id: string
  classID: string | null
  content: string
  description: string | null
  imageName: string
  name: string
  createdTime: string | null
  updatedTime: string | null
}

export type PlanningCourseV2Details = {
  courseID: string
  linkedServiceID: string | null
  sectionID: string
  templateID: string
  name: string
  length: number
  published: 'published' | 'unpublished'
  banner: string
  thumbnail: string
  // Syllabus bundle — empty string when unset
  description: string
  practiceGap: string
  practiceGapEvidence: string
  learnerEngagement: string
  learningOutcomes: string
  objectives: Objective[]
  references: Reference[]
  instructors: Instructor[]
  courseSteps: StreamStep[]
  // Legacy migration surface
  legacyClassID: string | null
  legacyTitleScreens: LegacyTitleScreen[]
  legacyCourseSteps: StreamStep[]
  legacyTextContent: TextContent[]
  version: number
  publishedVersion: number
}

export type CourseMigratableVideo = {
  id: string
  name: string
  description: string
  fileName: string
  isMigrated: boolean
}

export type CourseMigratableShowNote = {
  id: string
  name: string
  description: string
  isMigrated: boolean
}

export type CourseMigratableContent = {
  videos: CourseMigratableVideo[]
  showNotes: CourseMigratableShowNote[]
  quizExists: boolean
  quizMigrated: boolean
  capceExists: boolean
  capceMigrated: boolean
  anccExists: boolean
  anccMigrated: boolean
}

export type CourseV2Progress = {
  courseID: string
  courseName: string
  dataCompleteness: {
    fieldsCompleted: number
    fieldsTotal: number
    percentComplete: number
    missingFields: string[]
  }
  complianceStatus: {
    approved: number
    pending: number
    rejected: number
    notSubmitted: number
    percentApproved: number
    rejectedFields: string[]
    pendingFields: string[]
  }
  lastUpdated: string | null
}

export type NREMTLicense = {
  id: string
  userID: string
  imageName?: string
  type: 'EMR' | 'EMT' | 'AEMT' | 'Paramedic'
  nremtidNumber: string
  issueDate?: string
  expiration: string
}

export type EMSID = {
  id: string
  userID: string
  emsidNumber: string
} & TimeStamps

export type AssignmentCreditAuditAction =
  | 'ManagerCompleteAssignment'
  | 'ManagerCompleteAssignmentCourses'
  | 'ManagerCompleteAssignmentWithinThreshold'

export type AssignmentCreditAuditLog = {
  id: string
  actorUserID: string
  actorUserName: string
  targetUserID: string
  targetUserName: string
  action: AssignmentCreditAuditAction
  assignmentID: string
  assignmentName: string
  courseIDs: string[]
  courseNames: string[]
  data?: string
} & TimeStamps

export type VerificationStatus = 'pending' | 'accepted' | 'rejected'

export type VerificationEvent = {
  id: string
  actorID: string
  linkedServiceID: string
  status: VerificationStatus
  explanation?: string
} & TimeStamps

export type LicenseVerificationEvent = VerificationEvent & {
  licenseID: string
}

export type NREMTVerificationEvent = VerificationEvent & {
  nremtID: string
}

// Image Upload Types (for reusable image upload utilities)
export type ImageUploadType = 'banner' | 'thumbnail'

export type ImageUploadRequest = {
  id: string
  type: ImageUploadType
  contentType?: string // Optional: MIME type of the image file
}

export type ImageUploadResponse = {
  url: string // Presigned S3 URL
  imageName: string // Generated filename
}

// Recertification Cycle Types
export type RecertificationCycle = {
  id: string
  userID: string
  name: string
  issueDate: string
  expirationDate: string
  entity:
    | { kind: 'License'; license: License }
    | { kind: 'Credential'; credential: Credential }
    | { kind: 'NREMT'; nremt: NREMTLicense }
  complete: boolean
  totalHours?: number
  completedHours?: number
  templateID?: string
  description?: string
} & TimeStamps

export type RecertificationCycleType = RecertificationCycle['entity']['kind']

export type RecertificationCycleCourse = {
  id: string
  courseID: string
  categoryID: string
  hours: number
  required: boolean
  complete: boolean
} & TimeStamps

export type RecertificationCycleEvent = {
  id: string
  eventID: string
  categoryID: string
  hours: number
  complete: boolean
} & TimeStamps

export type RecertificationCycleExternalCertificate = {
  id: string
  externalCertificateID: string
  categoryID: string
  hours: number
} & TimeStamps

export type RecertificationCycleCategory = {
  id: string
  cycleID: string
  name: string
  subcategory?: string
  categoryType: 'standard' | 'live'
  hours: number
  sequence: number
  required: boolean
  courses: RecertificationCycleCourse[]
  events: RecertificationCycleEvent[]
  externalCertificates: RecertificationCycleExternalCertificate[]
} & TimeStamps

export type RecertificationCycleTemplateCategory = {
  id: string
  templateID: string
  name: string
  subcategory?: string
  categoryType: 'standard' | 'live'
  hours: number
  sequence: number
} & TimeStamps

export type RecertificationCycleTemplateCourse = {
  id: string
  templateCategoryID: string
  courseID: string
  hours: number
} & TimeStamps

export type RecertificationCycleTemplateCategoryWithCourses = {
  category: RecertificationCycleTemplateCategory
  courses: RecertificationCycleTemplateCourse[]
}

export type RecertificationCycleTemplateDetail = {
  template: RecertificationCycleTemplate
  categories: RecertificationCycleTemplateCategoryWithCourses[]
}

/** The certifications a template is for. No types means every cycle can apply it. */
export type RecertificationCycleTemplateTypes = {
  nremt: string[]
  credentials: string[]
  /** A license type with no states applies in any state. */
  licenses: { type: string; states: string[] }[]
}

export type RecertificationCycleTemplate = {
  id: string
  name: string
  description?: string
  linkedServiceID?: string
  categories: RecertificationCycleTemplateCategory[]
  /** Missing from create and update responses. */
  types?: RecertificationCycleTemplateTypes
} & TimeStamps

export type RecertCycleDetailItem = {
  id: string
  name: string
  hours: number
  required: boolean
  isComplete: boolean
}

export type RecertCycleDetailCategory = {
  id: string
  name: string
  subcategory: string | null
  categoryType: 'standard' | 'live'
  requiredHours: number
  completedHours: number
  isComplete: boolean
  courses: RecertCycleDetailItem[]
  liveEvents: RecertCycleDetailItem[]
  assignedEvents: RecertCycleDetailItem[]
  externalCertificates: RecertCycleDetailItem[]
}

export type RecertCycleDetail = {
  cycleID: string
  name: string
  issueDate: string
  expirationDate: string
  status: string | null
  totalRequiredHours: number
  totalCompletedHours: number
  categories: RecertCycleDetailCategory[]
}

// Compliance Types
export type AlertSetting = 'Disabled' | 'User' | 'Manager' | 'Both'

export type AlertType = 'Email' | 'Studio' | 'Both'

export type ComplianceRequirement = {
  id: string
  name: string
  linkedServiceID: string
  creatorID: string
  issueDate: string
  expirationDate: string
  alertSetting: AlertSetting
  alertType: AlertType
} & TimeStamps

export type ComplianceRequirementDetail = ComplianceRequirement & {
  userIDs: string[]
  licenseItems: { id: string; state: string; type: string }[]
  credentialItems: { id: string; type: string | { other: string } }[]
  nremtItems: { id: string }[]
  recertItems: {
    id: string
    cycleType:
      | 'nremt'
      | { license: { type: string; state: string } }
      | { credential: { type: string | { other: string } } }
  }[]
}

export type ComplianceItem = {
  id: string
  complianceID: string
} & TimeStamps

export type ComplianceLicenseItem = {
  id: string
  complianceItemID: string
  state: string
  type: string
} & TimeStamps

export type ComplianceCredentialItem = {
  id: string
  complianceItemID: string
  type: string
} & TimeStamps

export type RecertCycleType =
  | { kind: 'nremt' }
  | { kind: 'license'; type: string; state: string }
  | { kind: 'credential'; type: string }

export type ComplianceRecertificationItem = {
  id: string
  complianceItemID: string
  cycleType: RecertCycleType
  templateID?: string
} & TimeStamps

export type ComplianceUserItemJoin = {
  complianceItemID: string
  itemType: 'license' | 'credential' | 'nremt' | 'recert' | 'unknown'
  label: string
  status: string | null
  entityID: string | null
  entityLabel: string | null
}

export type ComplianceItemUserJoin = {
  userID: string
  status: string | null
  entityID: string | null
  entityLabel: string | null
}

export type ComplianceItemProgress = {
  complianceItemID: string
  label: string
  itemType: 'license' | 'credential' | 'nremt' | 'recert' | 'unknown'
  completedUsers: number
  totalUsers: number
}

export type ComplianceUserProgress = {
  userID: string
  completedItems: number
  startedItems: number
  totalItems: number
  isComplete: boolean
  completedAt: string | null
}

export type ComplianceProgress = {
  totalUsers: number
  completedUsers: number
  inProgressUsers: number
  notStartedUsers: number
  items: ComplianceItemProgress[]
  users: ComplianceUserProgress[]
}
